import React from 'react';
import styled from 'styled-components';
import { useTheme } from '../../context/ThemeContext';

const TextareaContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs};
`;

const Label = styled.label`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const StyledTextarea = styled.textarea`
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  line-height: ${({ theme }) => theme.typography.lineHeight.normal};
  color: ${({ theme }) => theme.colors.text};
  background-color: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ $hasError, theme }) => 
    $hasError ? theme.colors.error : theme.colors.border
  };
  border-radius: ${({ theme }) => theme.borderRadius.md};
  transition: border-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
  resize: vertical;
  min-height: 80px;
  
  &:focus {
    outline: none;
    border-color: ${({ $hasError, theme }) => 
      $hasError ? theme.colors.error : theme.colors.primary
    };
    box-shadow: 0 0 0 3px ${({ $hasError, theme }) => 
      $hasError ? theme.colors.errorLight : theme.colors.primaryLight
    }33;
  }
  
  &::placeholder {
    color: ${({ theme }) => theme.colors.textMuted};
  }
  
  &:disabled {
    background-color: ${({ theme }) => theme.colors.gray100};
    color: ${({ theme }) => theme.colors.textMuted};
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.error};
`;

const HelpText = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.textMuted};
`;

const CharacterCount = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.textMuted};
  text-align: right;
`;

export function Textarea({
  label,
  error,
  helpText,
  required = false,
  disabled = false,
  maxLength,
  showCharacterCount = false,
  className,
  ...props
}) {
  const { theme } = useTheme();
  const hasError = Boolean(error);
  const currentLength = props.value?.length || 0;

  return (
    <TextareaContainer className={className}>
      {label && (
        <Label>
          {label}
          {required && <span style={{ color: theme.colors.error }}> *</span>}
        </Label>
      )}
      <StyledTextarea
        $hasError={hasError}
        disabled={disabled}
        maxLength={maxLength}
        {...props}
      />
      {error && <ErrorMessage>{error}</ErrorMessage>}
      {helpText && !error && <HelpText>{helpText}</HelpText>}
      {showCharacterCount && maxLength && (
        <CharacterCount>
          {currentLength}/{maxLength}
        </CharacterCount>
      )}
    </TextareaContainer>
  );
}
